// Resume-where-you-left-off for the Bible & Apocrypha readers.
// Saved to localStorage on every device (instant, works signed-out) and,
// for signed-in users, mirrored to the backend so it syncs across devices.
// This module ONLY records/reads a position — it never touches the reader
// or voice/TTS logic.
import api from "./api";

const LS_KEY = (surface) => `hw_reading_progress_${surface}`;

export function saveProgressLocal(surface, data) {
  try {
    localStorage.setItem(LS_KEY(surface), JSON.stringify({ ...data, updated_at: new Date().toISOString() }));
  } catch (_) {}
}

export function getProgressLocal(surface) {
  try {
    const raw = localStorage.getItem(LS_KEY(surface));
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

// Persist the current position. Always writes locally; if authed, also
// upserts to the backend (fire-and-forget so it never blocks the reader).
export function saveProgress(surface, data, { authed } = {}) {
  saveProgressLocal(surface, data);
  if (authed) {
    api.put(`/reading-progress/${surface}`, {
      book: data.book,
      book_name: data.book_name || "",
      chapter: data.chapter,
      verse: data.verse || 1,
      version: data.version || "kjv",
      lang: data.lang || "en",
    }).catch(() => {});
  }
}

// Read the saved position. Prefers the backend for signed-in users
// (cross-device), falling back to the local copy if the request fails
// or the user is signed out.
export async function getProgress(surface, { authed } = {}) {
  if (authed) {
    try {
      const { data } = await api.get(`/reading-progress/${surface}`);
      if (data && data.book) {
        saveProgressLocal(surface, data);
        return data;
      }
    } catch (_) {}
  }
  return getProgressLocal(surface);
}

// ── List / reference pages (Precepts, Dictionary, Concordance) ─────────
// These pages have no per-entry URL, so "resume" restores the last
// search/filters and scrolls back to the entry you were on. Stored
// locally only (a device-level convenience). Rows must expose
// data-testid="<prefix><n>" and data-resume-label="<short label>".

// Topmost entry currently in view (first row whose bottom is below the header).
export function topVisibleResume(prefix, offset = 140) {
  const nodes = document.querySelectorAll(`[data-testid^="${prefix}"]`);
  for (const n of nodes) {
    const r = n.getBoundingClientRect();
    if (r.bottom > offset) {
      return { tid: n.getAttribute("data-testid"), label: n.getAttribute("data-resume-label") || "" };
    }
  }
  return null;
}

// Attach a debounced scroll listener that records the topmost entry +
// any extra filter state. Returns a cleanup fn for useEffect.
export function attachResumeTracker(surface, prefix, getExtra) {
  let last = null, t = null;
  const onScroll = () => {
    clearTimeout(t);
    t = setTimeout(() => {
      const top = topVisibleResume(prefix);
      if (top && top.tid && top.tid !== last) {
        last = top.tid;
        saveProgressLocal(surface, { ...top, ...(getExtra ? getExtra() : {}) });
      }
    }, 600);
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  return () => { window.removeEventListener("scroll", onScroll); clearTimeout(t); };
}
